import React, { useState, useEffect, useCallback, useRef } from 'react';
import { login as loginAdmin, logout as logoutAdmin, restoreSession } from '../../utils/adminSession';
import { Gallery } from '../../types/Gallery';
import { BlogPost } from '../../types/Blog';
import { DEFAULT_LOGO } from '../../config/defaults';
import { getPublicSiteUrl } from '../../utils/publicSiteUrl';
import { API_BASE_URL } from '../../utils/apiBase';
import AdminNavigation from './AdminNavigation';
import Dashboard from './Dashboard';
import GalleryManagement from './GalleryManagement';
import CategoryManagement from './CategoryManagement';
import BlogManagement from './BlogManagement';
import BlogCategoryManagement from './BlogCategoryManagement';
import ContentManagement from './ContentManagement';
import HomepageIntroEditor from './HomepageIntroEditor';
import LogoManagement from './LogoManagement';
import ShareImageManagement from './ShareImageManagement';
import SearchPhotosManagement from './SearchPhotosManagement';
import DisplaySettingsComponent from './DisplaySettings';
import Settings from './Settings';
import PackageManagement from './ServiceManagement';
import PackageCustomization from './PackageCustomization';
import CurrencyManagement from './CurrencyManagement';
import UserGuide from './UserGuide';
import SEODashboard from '../SEO/SEODashboard';
import ThemeManagement from './ThemeManagement';
import WelcomePopupManagement from './WelcomePopupManagement';
import MessagingTemplates from './MessagingTemplates';
import ClientGuidesManagement from './ClientGuidesManagement';
import QuoteBuilder from './QuoteBuilder';
import { loadMessageTemplates } from '../../utils/messageTemplateCache';
import QuoteHistory from './QuoteHistory';
import StudioBookings from './StudioBookings';
import StudioClients from './StudioClients';
import ShootFile from './ShootFile';
import { toShootTab, type ShootTab } from './shootTab';
import StudioPayments from './StudioPayments';
import StudioFinance from './StudioFinance';
import ClientRecord from './ClientRecord';
import StudioSettings from './StudioSettings';
import ReviewRequests from './ReviewRequests';
import ClientMessages from './ClientMessages';
import { StoredQuote } from '../../utils/quoteRecord';
import AddonManagement from './AddonManagement';
import InstagramManagement from './InstagramManagement';
import BackupManagement from './BackupManagement';
import LoadingAnimations from './LoadingAnimations';
import './AdminPage.css';
import '../../styles/studio-shell.css';
import './ui-enhancements.css';
import '../SEO/SEO.css';
import './admin-modals.css';

export type AdminSection = 
  | 'dashboard'
  | 'bookings'
  | 'clients'
  | 'payments'
  | 'review-requests'
  | 'galleries'
  | 'search-photos'
  | 'categories'
  | 'blogs'
  | 'blog-categories'
  | 'homepage'
  | 'content'
  | 'logo'
  | 'loading-animations'
  | 'share-image'
  | 'display-settings'
  | 'packages'
  | 'package-customization'
  | 'addons'
  | 'currency-management'
  | 'quote-builder'
  | 'quote-history'
  | 'client-messages'
  | 'shoot'
  | 'finance'
  | 'client'
  | 'settings'
  | 'studio-settings'
  | 'seo'
  | 'theme'
  | 'welcome-popup'
  | 'messaging'
  | 'client-guides'
  | 'instagram'
  | 'backup-management';

/** Lists that can open a shoot file and receive the Back link. */
export type ShootFromSection = 'dashboard' | 'clients' | 'client' | 'client-messages' | 'bookings' | 'quote-history' | 'payments' | 'review-requests' | 'finance';

const LIST_BACK_LABELS: Record<ShootFromSection, string> = {
  dashboard: 'Back to Today',
  clients: 'Back to clients',
  client: 'Back to client',
  'client-messages': 'Back to client messages',
  bookings: 'Back to calendar',
  'quote-history': 'Back to quotations',
  payments: 'Back to payments',
  'review-requests': 'Back to reviews',
  finance: 'Back to finance',
};

const sectionTitles: Record<AdminSection, string> = {
  dashboard: 'Today',
  bookings: 'Calendar',
  clients: 'Clients',
  payments: 'Payments',
  'review-requests': 'Reviews',
  galleries: 'Galleries',
  'search-photos': 'Photos for Google',
  categories: 'Gallery categories',
  blogs: 'Blog',
  'blog-categories': 'Blog categories',
  homepage: 'Homepage introduction',
  content: 'About Me',
  logo: 'Logo',
  'loading-animations': 'Loading animation',
  'share-image': 'Package share image',
  'display-settings': 'Display configurations',
  packages: 'Packages',
  'package-customization': 'Package text',
  addons: 'Package add-ons',
  'currency-management': 'Currency',
  'quote-builder': 'Quote builder',
  'quote-history': 'Quotations',
  'client-messages': 'Client messages',
  shoot: 'Shoot',
  finance: 'Finance',
  client: 'Client',
  settings: 'Share message',
  'studio-settings': 'Studio notifications',
  seo: 'SEO',
  theme: 'Colours',
  'welcome-popup': 'Welcome popup',
  messaging: 'Message templates',
  'client-guides': 'Client guides',
  instagram: 'Instagram',
  'backup-management': 'Backups',
};

function readShootFromUrl(): {
  section: AdminSection;
  quoteId: string;
  enquiryId: string;
  tab: ShootTab;
  from: ShootFromSection;
  clientKey: string;
} {
  const params = new URLSearchParams(window.location.search);
  const base = { quoteId: '', enquiryId: '', tab: 'money' as ShootTab, from: 'dashboard' as ShootFromSection, clientKey: '' };
  if (params.has('instagram_error') || params.has('instagram_connected')) {
    return { ...base, section: 'instagram' };
  }
  const shoot = params.get('shoot') || '';
  const enquiry = params.get('enquiry') || '';
  const tab = toShootTab(params.get('tab'));
  const fromRaw = params.get('from') || 'dashboard';
  const from = (Object.keys(LIST_BACK_LABELS).includes(fromRaw) ? fromRaw : 'dashboard') as ShootFromSection;
  if (shoot || enquiry) {
    return { ...base, section: 'shoot', quoteId: shoot, enquiryId: enquiry, tab, from };
  }
  const client = params.get('client') || '';
  if (client) {
    return { ...base, section: 'client', clientKey: client };
  }
  if (params.get('section') === 'client-messages') {
    return { ...base, section: 'client-messages' };
  }
  if (params.get('section') === 'finance') {
    return { ...base, section: 'finance' };
  }
  return { ...base, section: 'dashboard' };
}

function writeShootUrl(opts: {
  section: AdminSection;
  quoteId?: string;
  enquiryId?: string;
  tab?: ShootTab;
  from?: ShootFromSection;
  clientKey?: string;
  push?: boolean;
}) {
  const url = new URL(window.location.href);
  url.searchParams.delete('shoot');
  url.searchParams.delete('enquiry');
  url.searchParams.delete('tab');
  url.searchParams.delete('from');
  url.searchParams.delete('section');
  url.searchParams.delete('quote');
  url.searchParams.delete('client');
  if (opts.section === 'shoot') {
    if (opts.quoteId) url.searchParams.set('shoot', opts.quoteId);
    if (opts.enquiryId) url.searchParams.set('enquiry', opts.enquiryId);
    if (opts.tab) url.searchParams.set('tab', opts.tab);
    if (opts.from) url.searchParams.set('from', opts.from);
  } else if (opts.section === 'client' && opts.clientKey) {
    url.searchParams.set('client', opts.clientKey);
  } else if (opts.section === 'client-messages' || opts.section === 'finance') {
    url.searchParams.set('section', opts.section);
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  if (opts.push) window.history.pushState({}, '', next);
  else window.history.replaceState({}, '', next);
}

interface AdminPageProps {
  // Add any props here later
}

const AdminPage: React.FC<AdminPageProps> = () => {
  type StorageStatus = {
    storage: 'mongodb' | 'file' | 'unknown' | 'loading';
    message: string;
    error: string;
  };

  const initialUrl = readShootFromUrl();
  const [quoteDraft, setQuoteDraft] = useState<StoredQuote | null>(null);
  const [builderReturnClientKey, setBuilderReturnClientKey] = useState('');
  const [builderReturnQuoteId, setBuilderReturnQuoteId] = useState('');
  const [shootQuoteId, setShootQuoteId] = useState(initialUrl.quoteId);
  const [shootEnquiryId, setShootEnquiryId] = useState(initialUrl.enquiryId);
  const [shootTab, setShootTab] = useState<ShootTab>(initialUrl.tab);
  const [shootFrom, setShootFrom] = useState<ShootFromSection>(initialUrl.from);
  const [messageFilter, setMessageFilter] = useState('all');
  const [clientsStartEnquiry, setClientsStartEnquiry] = useState(false);
  const [clientsStage, setClientsStage] = useState('');
  const [clientRecordKey, setClientRecordKey] = useState(initialUrl.clientKey);
  const [paymentFocus, setPaymentFocus] = useState('');
  const [currentSection, setCurrentSection] = useState<AdminSection>(initialUrl.section);
  const currentSectionRef = useRef(currentSection);
  currentSectionRef.current = currentSection;
  const quoteLeaveGuard = useRef<(() => boolean) | null>(null);
  const registerQuoteLeaveGuard = useCallback((guard: (() => boolean) | null) => {
    quoteLeaveGuard.current = guard;
  }, []);
  const shootLeaveGuard = useRef<((proceed: () => void) => void) | null>(null);
  const registerShootLeaveGuard = useCallback((guard: ((proceed: () => void) => void) | null) => {
    shootLeaveGuard.current = guard;
  }, []);
  const shootQuoteIdRef = useRef(shootQuoteId);
  const shootEnquiryIdRef = useRef(shootEnquiryId);
  const shootTabRef = useRef(shootTab);
  const shootFromRef = useRef(shootFrom);
  shootQuoteIdRef.current = shootQuoteId;
  shootEnquiryIdRef.current = shootEnquiryId;
  shootTabRef.current = shootTab;
  shootFromRef.current = shootFrom;
  const allowShootLeave = (proceed: () => void) => {
    if (currentSectionRef.current === 'shoot' && shootLeaveGuard.current) {
      shootLeaveGuard.current(proceed);
      return;
    }
    proceed();
  };
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [logo, setLogo] = useState(DEFAULT_LOGO);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [storageStatus, setStorageStatus] = useState<StorageStatus>({
    storage: 'loading',
    message: 'Checking storage backend...',
    error: '',
  });

  useEffect(() => {
    document.body.classList.add('is-admin');
    return () => document.body.classList.remove('is-admin');
  }, []);

  useEffect(() => {
    document.body.classList.toggle('studio-drawer-open', isMobileMenuOpen);
    return () => document.body.classList.remove('studio-drawer-open');
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onPop = () => {
      const next = readShootFromUrl();
      if (
        currentSectionRef.current === 'quote-builder'
        && next.section !== 'quote-builder'
        && quoteLeaveGuard.current
        && !quoteLeaveGuard.current()
      ) {
        writeShootUrl({ section: 'quote-builder', push: true });
        return;
      }
      const leavingThisShoot = currentSectionRef.current === 'shoot' && (
        next.section !== 'shoot'
        || next.quoteId !== shootQuoteIdRef.current
        || next.enquiryId !== shootEnquiryIdRef.current
      );
      if (leavingThisShoot && shootLeaveGuard.current) {
        writeShootUrl({
          section: 'shoot',
          quoteId: shootQuoteIdRef.current,
          enquiryId: shootEnquiryIdRef.current,
          tab: shootTabRef.current,
          from: shootFromRef.current,
          push: true,
        });
        shootLeaveGuard.current(() => {
          setCurrentSection(next.section);
          setShootQuoteId(next.quoteId);
          setShootEnquiryId(next.enquiryId);
          setShootTab(next.tab);
          setShootFrom(next.from);
          setClientRecordKey(next.clientKey);
          writeShootUrl({
            section: next.section,
            quoteId: next.quoteId,
            enquiryId: next.enquiryId,
            tab: next.tab,
            from: next.from,
            clientKey: next.clientKey,
            push: false,
          });
        });
        return;
      }
      setCurrentSection(next.section);
      setShootQuoteId(next.quoteId);
      setShootEnquiryId(next.enquiryId);
      setShootTab(next.tab);
      setShootFrom(next.from);
      setClientRecordKey(next.clientKey);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const openShoot = (opts: {
    quoteId?: string;
    enquiryId?: string;
    tab?: ShootTab | string;
    from?: ShootFromSection;
  }) => {
    const go = () => {
      const tab = toShootTab(opts.tab);
      const from = opts.from || (currentSection === 'shoot' ? shootFrom : (currentSection as ShootFromSection));
      const safeFrom = (Object.keys(LIST_BACK_LABELS).includes(from) ? from : 'dashboard') as ShootFromSection;
      setShootQuoteId(opts.quoteId || '');
      setShootEnquiryId(opts.enquiryId || '');
      setShootTab(tab);
      setShootFrom(safeFrom);
      setCurrentSection('shoot');
      writeShootUrl({
        section: 'shoot',
        quoteId: opts.quoteId,
        enquiryId: opts.enquiryId,
        tab,
        from: safeFrom,
        push: true,
      });
    };
    const sameShoot = currentSection === 'shoot'
      && (opts.quoteId || '') === shootQuoteId
      && (opts.enquiryId || '') === shootEnquiryId;
    if (currentSection === 'shoot' && !sameShoot) allowShootLeave(go);
    else go();
  };

  const leaveShoot = (section: AdminSection = shootFrom) => {
    const go = () => {
      setShootQuoteId('');
      setShootEnquiryId('');
      setCurrentSection(section);
      writeShootUrl({ section, clientKey: section === 'client' ? clientRecordKey : undefined, push: true });
    };
    allowShootLeave(go);
  };

  /** The whole relationship with one person: every job, the money, and the history. */
  const openClientRecord = (clientKey: string) => {
    if (!clientKey) return;
    setClientRecordKey(clientKey);
    setCurrentSection('client');
    writeShootUrl({ section: 'client', clientKey, push: true });
  };

  useEffect(() => {
    let cancelled = false;
    restoreSession().then((session) => {
      if (cancelled || !session) return;
      setIsLoggedIn(true);
      loadLogo();
      loadMessageTemplates();
    });
    
    // Listen for logo updates
    const handleLogoUpdate = () => loadLogo();
    const handleGalleryUpdate = () => {
      loadGalleries();
    };
    
    window.addEventListener('logo_updated', handleLogoUpdate);
    window.addEventListener('storage', handleLogoUpdate);
    window.addEventListener('galleries_updated', handleGalleryUpdate);
    
    return () => {
      cancelled = true;
      window.removeEventListener('logo_updated', handleLogoUpdate);
      window.removeEventListener('storage', handleLogoUpdate);
      window.removeEventListener('galleries_updated', handleGalleryUpdate);
    };
  }, []);

  // Close mobile menu when clicking outside or when close event is triggered
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen && !(event.target as Element).closest('.admin-sidebar') && !(event.target as Element).closest('.hamburger-menu')) {
        setIsMobileMenuOpen(false);
      }
    };
    
    const handleCloseMenu = () => {
      setIsMobileMenuOpen(false);
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    window.addEventListener('closeMobileMenu', handleCloseMenu);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('closeMobileMenu', handleCloseMenu);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const needsGalleries = currentSection === 'galleries' || currentSection === 'categories' || currentSection === 'seo';
    if (needsGalleries) loadGalleries();
    if (currentSection === 'seo') loadBlogPosts();
    return undefined;
  }, [isLoggedIn, currentSection]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const fetchStorageStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/storage-status`, {
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const storage = result.storage === 'mongodb' ? 'mongodb' : (result.storage === 'file' ? 'file' : 'unknown');
        setStorageStatus({
          storage,
          message: result.message || 'Storage status available.',
          error: '',
        });
      } catch (error) {
        setStorageStatus({
          storage: 'unknown',
          message: 'Could not verify storage backend from server.',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    fetchStorageStatus();
    const intervalId = window.setInterval(fetchStorageStatus, 60000);

    return () => window.clearInterval(intervalId);
  }, [isLoggedIn]);

  const loadLogo = () => {
    const logoData = localStorage.getItem('website_logo');
    if (logoData) {
      try {
        const logoParsed = JSON.parse(logoData);
        setLogo(prev => ({ ...prev, ...logoParsed }));
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    }
  };

  const loadGalleries = async () => {
    try {
      // LIVE MODE: Only load from server - no localStorage fallbacks
      const { loadLiveGalleries } = await import('../../utils/GalleryAPI');
      const serverGalleries = await loadLiveGalleries();
      
      setGalleries(serverGalleries);
      
    } catch (error) {
      console.error('Error loading galleries:', error);
      setGalleries([]);
    }
  };

  const loadBlogPosts = async () => {
    try {
      const { getBlogPostsFromServer } = await import('../../utils/BlogAPI');
      const serverPosts = await getBlogPostsFromServer();
      setBlogPosts(serverPosts);
      localStorage.setItem('admin_blog_posts', JSON.stringify(serverPosts));
    } catch (error) {
      console.error('Error loading blog posts:', error);
      setBlogPosts([]);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    
    const formData = new FormData(e.target as HTMLFormElement);
    const username = formData.get('username') as string;
    const password = formData.get('password') as string;
    const result = await loginAdmin(username, password);
    if (result.ok) {
      // Ask the browser to offer to remember this sign-in (Chrome, Edge and Android).
      // Other browsers offer to save it themselves once the sign-in form goes away.
      try {
        const Credential = (window as any).PasswordCredential;
        if (Credential && navigator.credentials) {
          await navigator.credentials.store(new Credential({ id: username, password, name: username }));
        }
      } catch (_) {
        // Saving a password is a convenience only; never block signing in.
      }
      setIsLoggedIn(true);
      loadLogo();
    } else {
      setLoginError(result.error);
    }
  };

  const handleLogout = async () => {
    const go = async () => {
      if (quoteLeaveGuard.current && !quoteLeaveGuard.current()) return;
      await logoutAdmin();
      setIsLoggedIn(false);
    };
    if (currentSectionRef.current === 'shoot') allowShootLeave(() => { void go(); });
    else void go();
  };

  const handleSectionChange = (section: AdminSection) => {
    const go = () => {
      if (
        currentSection === 'quote-builder'
        && section !== currentSection
        && quoteLeaveGuard.current
        && !quoteLeaveGuard.current()
      ) {
        return;
      }
      applySection(section);
    };
    if (currentSection === 'shoot' && section !== 'shoot') allowShootLeave(go);
    else go();
  };

  const applySection = (section: AdminSection) => {
    if (section === 'quote-builder') setQuoteDraft(null);
    setShootQuoteId('');
    setShootEnquiryId('');
    setClientsStartEnquiry(false);
    setCurrentSection(section);
    writeShootUrl({ section, push: true });
  };

    if (!isLoggedIn) {
    return (
      <div className="login-screen">
        <div className="login-container">
          <div className="login-header">
            <img className="login-mark" src="/brand/carmen-lorraine-mark.svg" alt="" />
            <p className="login-kicker">Carmen Lorraine Photography</p>
            <h1>Studio</h1>
            <p>Sign in to quotations, bookings and messages.</p>
          </div>
          <form className="login-form" method="post" action="#" onSubmit={handleLogin}>
            {loginError && (
              <div className="login-error" role="alert">
                {loginError}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input type="text" id="username" name="username" autoComplete="username" required />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-field">
                <input type={showPassword ? 'text' : 'password'} id="password" name="password" autoComplete="current-password" required />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-pressed={showPassword}
                  aria-controls="password"
                >
                  {showPassword ? 'Hide' : 'Show'}
                  <span className="sr-only"> password</span>
                </button>
              </div>
            </div>
            <button type="submit" className="login-btn">Sign in</button>
          </form>
        </div>
      </div>
    );
  }

  const openBuilder = (quote?: StoredQuote | null, returnClientKey?: string, returnQuoteId?: string, editIssued = false) => {
    const go = () => {
      const revising = Boolean(editIssued && quote?.id && (quote.status === 'issued' || quote.status === 'accepted'));
      if (quote && quote.status && quote.status !== 'draft' && quote.id && !revising) {
        // Issued/accepted open on the shoot file, not the builder.
        openShoot({ quoteId: quote.id, tab: 'money', from: shootFrom });
        return;
      }
      setQuoteDraft(quote || null);
      setBuilderReturnClientKey(returnClientKey || '');
      setBuilderReturnQuoteId(returnQuoteId || quote?.id || shootQuoteId || '');
      setCurrentSection('quote-builder');
      writeShootUrl({ section: 'quote-builder', push: true });
    };
    if (currentSectionRef.current === 'shoot') allowShootLeave(go);
    else go();
  };

  const renderCurrentSection = () => {
    switch (currentSection) {
      case 'dashboard':
        return (
          <Dashboard
            onOpenShoot={(quoteId, tab) => openShoot({ quoteId, tab, from: 'dashboard' })}
            onOpenEnquiry={(enquiryId) => openShoot({ enquiryId, from: 'dashboard' })}
            onStartEnquiry={() => {
              setClientsStartEnquiry(true);
              setShootQuoteId('');
              setShootEnquiryId('');
              setCurrentSection('clients');
              writeShootUrl({ section: 'clients', push: true });
            }}
            onOpen={(section, quoteId) => {
              if (section === 'payments') {
                setPaymentFocus(quoteId && quoteId.length > 12 ? '' : (quoteId || ''));
                setCurrentSection('payments');
                writeShootUrl({ section: 'payments', push: true });
                return;
              }
              if (section === 'client-messages') {
                setMessageFilter('awaiting-carmen');
                setCurrentSection('client-messages');
                writeShootUrl({ section: 'client-messages', push: true });
                return;
              }
              if (section === 'quote-builder') {
                openBuilder(null);
                return;
              }
              if (section === 'clients' && quoteId && quoteId.startsWith('stage:')) {
                setClientsStage(quoteId.slice('stage:'.length));
                setCurrentSection('clients');
                writeShootUrl({ section: 'clients', push: true });
                return;
              }
              if (quoteId && (section === 'clients' || section === 'quote-history' || section === 'bookings')) {
                openShoot({ quoteId, tab: 'money', from: 'dashboard' });
                return;
              }
              if (section === 'clients') {
                setClientsStage('');
              }
              setCurrentSection(section);
              writeShootUrl({ section, push: true });
            }}
          />
        );
      case 'finance':
        return (
          <StudioFinance
            onOpenPayments={(focus) => {
              setPaymentFocus(focus || '');
              setCurrentSection('payments');
              writeShootUrl({ section: 'payments', push: true });
            }}
            onOpenClients={(stage) => {
              setClientsStage(stage || '');
              setCurrentSection('clients');
              writeShootUrl({ section: 'clients', push: true });
            }}
          />
        );
      case 'client':
        return (
          <ClientRecord
            key={clientRecordKey}
            clientKey={clientRecordKey}
            onBack={() => {
              setCurrentSection('clients');
              writeShootUrl({ section: 'clients', push: true });
            }}
            onOpenShoot={(quoteId, tab) => openShoot({ quoteId, tab, from: 'client' })}
            onOpenEnquiry={(enquiryId) => openShoot({ enquiryId, from: 'client' })}
            onOpenBuilder={() => openBuilder(null, clientRecordKey)}
          />
        );
      case 'bookings':
        return (
          <StudioBookings
            onOpenQuotation={(quoteId) => openShoot({ quoteId, tab: 'booking', from: 'bookings' })}
          />
        );
      case 'clients':
        return (
          <StudioClients
            startWithEnquiry={clientsStartEnquiry}
            onConsumedStart={() => setClientsStartEnquiry(false)}
            initialStage={clientsStage}
            onOpenClientRecord={openClientRecord}
            onOpenShoot={(opts) => openShoot({ ...opts, from: 'clients' })}
            onOpenBuilder={(quote, returnClientKey, editIssued) => openBuilder(quote, returnClientKey, '', Boolean(editIssued))}
            onOpenCalendar={() => {
              setCurrentSection('bookings');
              writeShootUrl({ section: 'bookings', push: true });
            }}
          />
        );
      case 'payments':
        return (
          <StudioPayments
            focus={paymentFocus}
            onOpenShoot={(quoteId) => openShoot({ quoteId, tab: 'money', from: 'payments' })}
          />
        );
      case 'review-requests':
        return (
          <ReviewRequests
            onOpenShoot={(quoteId) => openShoot({ quoteId, tab: 'money', from: 'review-requests' })}
          />
        );
      case 'galleries':
        return <GalleryManagement galleries={galleries} onGalleriesChange={setGalleries} />;
      case 'search-photos':
        return <SearchPhotosManagement />;
      case 'categories':
        return <CategoryManagement galleries={galleries} />;
      case 'blogs':
        return <BlogManagement />;
      case 'blog-categories':
        return <BlogCategoryManagement />;
      case 'homepage':
        return <HomepageIntroEditor />;
      case 'content':
        return <ContentManagement />;
      case 'logo':
        return <LogoManagement />;
      case 'loading-animations':
        return <LoadingAnimations />;
      case 'share-image':
        return <ShareImageManagement />;
      case 'display-settings':
        return <DisplaySettingsComponent />;
      case 'packages':
        return <PackageManagement />;
      case 'package-customization':
        return <PackageCustomization />;
      case 'addons':
        return <AddonManagement />;
      case 'currency-management':
        return <CurrencyManagement />;
      case 'quote-builder':
        return (
          <QuoteBuilder
            initialDraft={quoteDraft}
            onLeaveGuard={registerQuoteLeaveGuard}
            returnLabel={builderReturnQuoteId || builderReturnClientKey ? 'Back to shoot' : 'Back to quotations'}
            onDone={(saved) => {
              const returnId = saved?.id || builderReturnQuoteId || quoteDraft?.id || '';
              const key = builderReturnClientKey;
              setQuoteDraft(null);
              setBuilderReturnClientKey('');
              setBuilderReturnQuoteId('');
              if (returnId) {
                openShoot({
                  quoteId: returnId,
                  tab: 'money',
                  from: key ? 'clients' : 'quote-history',
                });
                return;
              }
              leaveShoot('quote-history');
            }}
          />
        );
      case 'quote-history':
        return (
          <QuoteHistory
            onOpenBuilder={(quote, editIssued) => openBuilder(quote, '', '', Boolean(editIssued))}
            onOpenShoot={(quoteId, tab) => openShoot({ quoteId, tab: tab || 'money', from: 'quote-history' })}
          />
        );
      case 'client-messages':
        return (
          <ClientMessages
            initialFilter={messageFilter}
            onOpenShoot={(quoteId) => openShoot({ quoteId, tab: 'conversation', from: 'client-messages' })}
          />
        );
      case 'shoot':
        return (
          <ShootFile
            key={`${shootQuoteId}|${shootEnquiryId}`}
            quoteId={shootQuoteId}
            enquiryId={shootEnquiryId}
            initialTab={shootTab}
            backLabel={LIST_BACK_LABELS[shootFrom] || 'Back'}
            onBack={() => leaveShoot(shootFrom)}
            onLeaveGuard={registerShootLeaveGuard}
            onOpenCalendar={() => {
              allowShootLeave(() => {
                setCurrentSection('bookings');
                writeShootUrl({ section: 'bookings', push: true });
              });
            }}
            onOpenBuilder={(quote, returnClientKey, editIssued) => openBuilder(quote, returnClientKey, shootQuoteId, Boolean(editIssued))}
            onOpenShoot={(quoteId, tab) => openShoot({ quoteId, tab, from: shootFrom })}
            onTabChange={(tab) => {
              setShootTab(tab);
              writeShootUrl({
                section: 'shoot',
                quoteId: shootQuoteId,
                enquiryId: shootEnquiryId,
                tab,
                from: shootFrom,
                push: false,
              });
            }}
          />
        );
      case 'seo':
        return <SEODashboard galleries={galleries} blogPosts={blogPosts} baseUrl={getPublicSiteUrl()} />;
      case 'theme':
        return <ThemeManagement />;
      case 'settings':
        return <Settings storageStatus={storageStatus} />;
      case 'studio-settings':
        return <StudioSettings />;
      case 'welcome-popup':
        return <WelcomePopupManagement />;
      case 'messaging':
        return <MessagingTemplates />;
      case 'client-guides':
        return <ClientGuidesManagement />;
      case 'instagram':
        return <InstagramManagement />;
      case 'backup-management':
        return <BackupManagement />;
      default:
        return <Dashboard onOpen={(section) => setCurrentSection(section)} />;
    }
  };

  return (
    <div className="admin-dashboard admin-page-container studio-shell">
      <header className={`admin-actions-bar ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="admin-actions-content">
          <button 
            className={`hamburger-menu ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            type="button"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className="admin-title">
            <p className="admin-kicker">Studio</p>
            <h1>{currentSection === 'shoot' ? 'Shoot' : sectionTitles[currentSection]}</h1>
          </div>
          <div className="admin-actions">
            <button 
              className="btn btn-quiet btn-help"
              onClick={() => setShowUserGuide(true)}
              title="Open user guide"
            >
              <i className="fas fa-question-circle" aria-hidden="true"></i>
              <span>Help</span>
            </button>
            <button 
              className="btn btn-quiet btn-preview"
              onClick={() => window.open('/', '_blank')}
            >
              <i className="fas fa-eye" aria-hidden="true"></i>
              <span>Preview Site</span>
            </button>
            <button 
              className="btn btn-quiet btn-logout"
              onClick={handleLogout}
            >
              <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="admin-content-wrapper">
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="admin-sidebar-overlay active"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        
        <aside className={`admin-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <AdminNavigation
            currentSection={currentSection === 'shoot' ? shootFrom : currentSection}
            onSectionChange={(section) => {
              handleSectionChange(section);
              setIsMobileMenuOpen(false);
            }}
            isMobileMenuOpen={isMobileMenuOpen}
            onOpenHelp={() => {
              setShowUserGuide(true);
              setIsMobileMenuOpen(false);
            }}
            onPreviewSite={() => window.open('/', '_blank')}
            onLogout={() => {
              setIsMobileMenuOpen(false);
              handleLogout();
            }}
          />
        </aside>

        <main className={`admin-main ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          {renderCurrentSection()}
        </main>
      </div>

      {/* User Guide Modal */}
      {showUserGuide && (
        <UserGuide 
          onClose={() => setShowUserGuide(false)}
          currentSection={currentSection}
        />
      )}
    </div>
  );
};

export default AdminPage;
